import BatteryChargingFullIcon from '@mui/icons-material/BatteryChargingFull';
import PrintIcon from '@mui/icons-material/Print';
import QuestionMarkIcon from '@mui/icons-material/QuestionMark';
import RefreshIcon from '@mui/icons-material/Refresh';
import {
  Alert,
  Backdrop,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Menu,
  MenuItem,
  Tab,
  Tabs,
  Tooltip,
} from '@mui/material';
import dayjs from 'dayjs';
import update from 'immutability-helper';
import PopupState, {bindMenu, bindTrigger} from 'material-ui-popup-state';
import {useCallback, useEffect, useMemo, useRef, useState} from 'react';

import API, {getMessagesFromApiError} from '../../../api/axios';
import {apiBaseUrl} from '../../../api/urls';
import {usePrint} from '../../../hooks/print';
import {useAppDispatch, useAppSelector} from '../../../hooks/redux';
import {useRefreshInterval} from '../../../hooks/refreshInterval';
import useShiftSelect, {isShiftClick} from '../../../hooks/useShiftSelect';
import {
  CommtracNodeHistory,
  CommtracNodeHistoryListQuery,
  CommtracNodeHistoryListResponse,
} from '../../../interfaces/CommtracNodeHistory';
import reduxActions from '../../../redux/actions';
import {getBooleanValue} from '../../../utils/boolean';
import {
  employeeMessageTypes,
  getCommtracNodeHistoryId,
} from '../../../utils/message-types';
import {
  PRINT_HIDDEN_SX,
  PRINTABLE_CONTAINER_SX,
  PRINTABLE_MAP_SX,
} from '../../../utils/printStyles';
import {
  handleChangeTab,
  handleCloseTab,
  handleOpenTab,
} from '../../../utils/tab';
import {AdaptiveButtonGroup} from '../../common/AdaptiveButtonGroup';
import {AutoRefreshSelect} from '../../common/AutoRefreshSelect';
import DataGrid, {DataGridColumn, DataGridRef} from '../../common/DataGrid';
import {Map, MapLayerId} from '../../common/Map';
import {ResizableColumns} from '../../common/ResizableColumns';
import TabLabel from '../../common/TabLabel';
import TruncatedText from '../../common/TruncantedText';
import CommtracNodeHistoryExportButton from '../../commtrac-nodes/CommtracNodeHistoryExportButton';
import {DateRangeSelect} from '../../selectors/DateRangeSelect';
import MessageTypeSelect from '../../selectors/MessageTypeSelect';
import {DashboardPanelTitleSlot} from '../DashboardPanelTitleSlot';

interface Props {
  value?: EmployeeTrackingReportsData;
  onUpdate?: (value?: EmployeeTrackingReportsData) => void;
}

const DEFAULT_SHOWN_FIELDS = [
  'battery_voltage',
  'timestamp',
  'message_type',
  'motion_sensor_value',
  'message',
  'status',
  'section',
  'strongest_cn_name',
];

interface EmployeeTrackingReportsDataTab {
  id: number;
  refreshInterval?: number | null;
  selectedIds?: string[];
  selectAll: boolean;
  params?: {
    date_start?: string;
    date_end?: string;
    message_type?: number[];
    page?: number;
    limit?: number;
    order?: string;
    dir?: 'ASC' | 'DESC';
  };
  columnWidths: Record<string, number>;
  columnOrder?: string[];
  shownFields?: string[];
}

export interface EmployeeTrackingReportsData {
  activeId?: number;
  mapLayers?: MapLayerId[];
  mapLevel?: number | null;
  openedItems: EmployeeTrackingReportsDataTab[];
}

export const getEmployeeTrackingReportsData =
  (): EmployeeTrackingReportsData => ({
    activeId: undefined,
    mapLayers: ['employee_history', 'mine'],
    mapLevel: null,
    openedItems: [],
  });

const getEmployeeTrackingReportsDataTab = (
  id: number
): EmployeeTrackingReportsDataTab => ({
  id,
  selectAll: true,
  params: {
    date_start: dayjs().format('YYYY-MM-DD'),
    date_end: dayjs().format('YYYY-MM-DD'),
  },
  columnWidths: {},
  columnOrder: undefined,
  shownFields: DEFAULT_SHOWN_FIELDS,
});

export const EmployeeTrackingReports = ({value, onUpdate}: Props) => {
  const reduxDispatch = useAppDispatch();

  const isOpenAwayFromConnectView = useMemo(() => {
    return (
      location.pathname.includes('/panels/') ||
      !document.getElementById('connect-view-panel')
    );
  }, [location, value]);

  const config = useMemo(() => {
    const v = value ?? getEmployeeTrackingReportsData();
    return {
      ...v,
      mapLayers: v.mapLayers ?? ['employee_history', 'street', 'mine'],
      mapLevel: v.mapLevel ?? null,
    };
  }, [value]);

  const openedItemIndex = useMemo(
    () =>
      (config.activeId
        ? config.openedItems.findIndex((i) => i.id === config.activeId)
        : null) ?? config.openedItems.length - 1,
    [config.activeId, config.openedItems]
  );

  const openedItem = useMemo(
    () =>
      openedItemIndex !== -1
        ? config.openedItems[openedItemIndex]
          ? config.openedItems[openedItemIndex]
          : getEmployeeTrackingReportsDataTab(config.activeId as number)
        : null,
    [openedItemIndex, config.openedItems]
  );

  const commtracNodes = useAppSelector(({assets}) =>
    assets.commtrac_nodes.filter(
      (i) => i.type === 'miner' && i.commtrac_ack === '1' && i.employee_asset_id
    )
  );

  const tabNames = useMemo(
    () =>
      config.openedItems.map(
        (o) => commtracNodes.find((i) => i.id === o.id)?.name
      ),
    [config.openedItems, commtracNodes]
  );

  // get fresh assets
  useEffect(() => {
    reduxDispatch(reduxActions.assets.fetchCommtracNodes);
  }, [config.openedItems]);

  /*********/
  /* fetch */
  /*********/
  const [fetchedData, setFetchedData] =
    useState<CommtracNodeHistoryListResponse>();
  const [fetchedErrors, setFetchedErrors] = useState<string[]>([]);
  const [fetchedInProgress, setFetchedInProgress] = useState(false);

  const params = useMemo<CommtracNodeHistoryListQuery | null>(
    () =>
      openedItem?.id
        ? {
            date_start:
              openedItem.params?.date_start ?? dayjs().format('YYYY-MM-DD'),
            date_end:
              openedItem.params?.date_end ?? dayjs().format('YYYY-MM-DD'),
            message_type: openedItem.params?.message_type ?? [],
            page: openedItem.params?.page ?? 0,
            limit: openedItem.params?.limit ?? 25,
            order: openedItem.params?.order ?? 'date',
            dir: openedItem.params?.dir ?? 'DESC',
            activeId: openedItem.id,
          }
        : null,
    [openedItem?.id, openedItem?.params]
  );

  const fetchData = useCallback(
    async (params: CommtracNodeHistoryListQuery) => {
      setFetchedInProgress(true);
      setFetchedErrors([]);
      try {
        const endpoint = `${apiBaseUrl}/commtrac-node/${config.activeId}/history`;
        const resp = await API.get<CommtracNodeHistoryListResponse>(endpoint, {
          params,
        });
        setFetchedData(resp.data);
        // collect all IDs
        if (openedItem && openedItem.selectAll) {
          openedItem.selectedIds = resp.data.items.map((el) =>
            getCommtracNodeHistoryId(el)
          );
        }
      } catch (error: any) {
        const messages = getMessagesFromApiError(error, 'STRA_d634bfa7');
        setFetchedErrors(messages);
      }
      setFetchedInProgress(false);
    },
    [params]
  );

  useEffect(() => {
    if (params) {
      fetchData(params);
    }
  }, [params]);

  useEffect(() => {
    if (!openedItem) {
      setFetchedData(undefined);
    }
  }, [openedItem]);

  /****************/
  /* auto refresh */
  /****************/

  const callFetchData = useCallback(() => {
    params && fetchData(params);
  }, [params]);

  useRefreshInterval(callFetchData, openedItem?.refreshInterval);

  /*********/
  /* grid */
  /*********/
  const shownFields = useMemo(
    () => openedItem?.shownFields ?? DEFAULT_SHOWN_FIELDS,
    [openedItem?.shownFields]
  );
  const dataGridRef = useRef<DataGridRef>(null);
  const printContainerRef = useRef<HTMLDivElement>(null);
  const [printReport] = usePrint(printContainerRef.current);
  const rows = fetchedData?.items ?? [];
  const columns: DataGridColumn<CommtracNodeHistory>[] = [
    {
      field: 'select',
      type: 'select',
      width: 60,
      hideable: false,
      renderHeader: () => (
        <Checkbox
          color="primary"
          disabled={rows.length === 0}
          checked={selectedItems.length > 0 && selectedAll}
          indeterminate={selectedItems.length > 0 && !selectedAll}
          onChange={() => toggleSelectAllItems()}
        />
      ),
      renderCell: ({row, visibleRows}) => (
        <Checkbox
          color="primary"
          checked={selectedItems.includes(getCommtracNodeHistoryId(row))}
          onChange={(event) =>
            toggleSelectItem(
              getCommtracNodeHistoryId(row),
              isShiftClick(event),
              visibleRows.map((visibleRow) =>
                getCommtracNodeHistoryId(visibleRow)
              )
            )
          }
        />
      ),
    },
    {
      field: 'battery_voltage',
      renderHeader: () => (
        <Tooltip title="Battery Level">
          <BatteryChargingFullIcon />
        </Tooltip>
      ),
      sortable: false,
      width: 40,
      valueGetter: ({row}) =>
        row.battery_voltage ? row.battery_voltage / 10 : null,
      renderCell: ({value}) => {
        if (value === null) {
          return <QuestionMarkIcon fontSize="small" />;
        }
        return <>{value}</>;
      },
    },
    {
      field: 'timestamp',
      headerName: 'Timestamp',
      width: 180,
      resizeable: true,
      sortable: true,
      renderCell: ({row, value}) => {
        return (
          <TruncatedText
            color={getBooleanValue(row.alarm, 0, 1) ? 'error.main' : undefined}
            fontWeight={getBooleanValue(row.alarm, 0, 1) ? 'bold' : undefined}
          >
            {value?.toString()}
          </TruncatedText>
        );
      },
    },
    {
      field: 'message_type',
      headerName: 'Message Type',
      width: 120,
      resizeable: true,
      sortable: true,
      renderCell: ({row, value}) => {
        return (
          <TruncatedText
            color={getBooleanValue(row.alarm, 0, 1) ? 'error.main' : undefined}
            fontWeight={getBooleanValue(row.alarm, 0, 1) ? 'bold' : undefined}
          >
            {value?.toString()}
          </TruncatedText>
        );
      },
    },
    {
      field: 'motion_sensor_value',
      headerName: 'Motion State',
      width: 120,
      resizeable: true,
      sortable: true,
    },
    {
      field: 'message',
      headerName: 'Message',
      width: 100,
      resizeable: true,
      sortable: true,
    },
    {
      field: 'message_id',
      headerName: 'Message ID',
      width: 120,
      resizeable: true,
      sortable: true,
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 100,
      resizeable: true,
      sortable: true,
    },
    {
      field: 'section',
      headerName: 'Section',
      width: 120,
      resizeable: true,
      sortable: true,
    },
    {
      field: 'strongest_cn_name',
      width: 150,
      resizeable: true,
      headerName: 'Strongest Node',
      sortable: true,
    },
    {
      field: 'longitude',
      headerName: 'Longitude',
      width: 120,
      resizeable: true,
      sortable: true,
    },
    {
      field: 'latitude',
      headerName: 'Latitude',
      width: 120,
      resizeable: true,
      sortable: true,
    },
    {
      field: 'beacon_data',
      headerName: 'Beacon Data',
      width: 200,
      resizeable: true,
      sortable: true,
    },
  ];

  const exportFields = useMemo(() => {
    return columns
      .filter((col) => !!col.headerName && !col.doNotExport)
      .map((col) => ({
        field: col.field,
        label: col.headerName,
        hidden: !shownFields?.includes(col.field),
      }));
  }, [shownFields]);

  /*******************/
  /* multiple select */
  /*******************/
  const selectedItems = useMemo(
    () =>
      openedItem?.selectAll
        ? rows.map((it) => getCommtracNodeHistoryId(it))
        : openedItem?.selectedIds ?? [],
    [rows, openedItem]
  );

  const selectedRows = useMemo(
    () =>
      rows.filter((i) => selectedItems?.includes(getCommtracNodeHistoryId(i))),
    [rows, selectedItems]
  );

  const selectedAll = useMemo(
    () => rows.length === selectedRows.length,
    [rows, selectedRows]
  );

  const getNextSelectedIds = useShiftSelect<string>();

  const toggleSelectItem = (
    id: string,
    shiftKey: boolean,
    visibleIds: string[]
  ) => {
    if (openedItem) {
      const nextSelectedIds = getNextSelectedIds({
        id,
        visibleIds,
        selectedIds: selectedItems ?? [],
        shiftKey,
      });
      onUpdate?.(
        update(config, {
          openedItems: {
            [openedItemIndex]: {
              $set: {
                ...openedItem,
                selectedIds: nextSelectedIds,
                selectAll: nextSelectedIds.length === fetchedData?.items.length,
              },
            },
          },
        })
      );
    }
  };

  const selectAll = () => {
    if (openedItem) {
      onUpdate?.(
        update(config, {
          openedItems: {
            [openedItemIndex]: {
              selectAll: {
                $set: true,
              },
              selectedIds: {
                $set: rows?.map((i) => getCommtracNodeHistoryId(i)) ?? [],
              },
            },
          },
        })
      );
    }
  };

  const unselectAll = () => {
    if (openedItem) {
      onUpdate?.(
        update(config, {
          openedItems: {
            [openedItemIndex]: {
              selectAll: {
                $set: false,
              },
              selectedIds: {
                $set: [],
              },
            },
          },
        })
      );
    }
  };

  const toggleSelectAllItems = () => {
    if (selectedItems.length >= rows.length) {
      unselectAll();
    } else {
      selectAll();
    }
  };

  const openTab = (id: number) => {
    return handleOpenTab(id, config, onUpdate);
  };

  const closeTab = (id: number) => {
    return handleCloseTab(id, config, onUpdate);
  };

  const changeTab = (_: any, id: number) => {
    handleChangeTab(id, config, onUpdate);
  };

  /************/
  /* map data */
  /************/
  const mapData = useMemo(() => {
    if (fetchedData?.items && selectedItems.length) {
      return fetchedData?.items.filter((i) =>
        selectedItems.includes(getCommtracNodeHistoryId(i))
      );
    }
    return [];
  }, [fetchedData, selectedItems, selectedItems]);

  const handleColumnWidthChange = (widths: Record<string, number>) => {
    if (openedItem) {
      onUpdate?.(
        update(config, {
          openedItems: {
            [openedItemIndex]: {
              $set: {
                ...openedItem,
                columnWidths: widths,
              },
            },
          },
        })
      );
    }
  };

  const handleShownFieldsChange = useCallback(
    (fields: string[]) => {
      if (openedItem) {
        onUpdate?.(
          update(config, {
            openedItems: {
              [openedItemIndex]: {
                shownFields: {$set: fields},
              },
            },
          })
        );
      }
    },
    [onUpdate, config, openedItemIndex, openedItem]
  );

  const handleColumnOrderAndShownFieldsChange = useCallback(
    (order: string[], fields: string[]) => {
      if (openedItem) {
        onUpdate?.(
          update(config, {
            openedItems: {
              [openedItemIndex]: {
                columnOrder: {$set: order},
                shownFields: {$set: fields},
              },
            },
          })
        );
      }
    },
    [onUpdate, config, openedItemIndex, openedItem]
  );

  return (
    <>
      <DashboardPanelTitleSlot>
        Employee Tracking Reports
      </DashboardPanelTitleSlot>

      <Box ref={printContainerRef} sx={PRINTABLE_CONTAINER_SX}>
        <ResizableColumns
          left={
            <Map
              sx={PRINTABLE_MAP_SX}
              panel="employee_tracking_report"
              employeeHistoryData={mapData}
              selectedLevel={config.mapLevel}
              selectedMapLayers={config.mapLayers}
              availableMapLayers={['street', 'mine', 'employee_history']}
              onSelectMapLayers={(v) => {
                onUpdate?.(
                  update(config, {
                    mapLayers: {
                      $set: v,
                    },
                  })
                );
              }}
              onSelectLevel={(v) => {
                onUpdate?.(
                  update(config, {
                    mapLevel: {
                      $set: v,
                    },
                  })
                );
              }}
            />
          }
        >
          <Box
            display="flex"
            flexDirection="column"
            height="100%"
            width="100%"
            overflow="hidden"
          >
            <Box
              sx={PRINT_HIDDEN_SX}
              display="flex"
              flexDirection="column"
              gap={3}
              py={1.5}
              bgcolor={(theme) =>
                theme.palette.mode === 'dark' ? '#2E2E2E' : '#FFF'
              }
            >
              {isOpenAwayFromConnectView && (
                <Box pr={1.5}>
                  <PopupState variant="popover" popupId="demo-popup-menu">
                    {(popupState) => (
                      <>
                        <Button variant="outlined" {...bindTrigger(popupState)}>
                          Select an Object
                        </Button>
                        <Menu {...bindMenu(popupState)}>
                          {commtracNodes?.map((it, key: number) => (
                            <MenuItem
                              key={key}
                              onClick={() => {
                                it?.id && openTab(it.id);
                                popupState.close();
                              }}
                            >
                              {it.name}
                            </MenuItem>
                          ))}
                        </Menu>
                      </>
                    )}
                  </PopupState>
                </Box>
              )}
              {config.openedItems.length ? (
                <Box pr={2}>
                  <Tabs
                    value={openedItem?.id}
                    variant="scrollable"
                    onChange={changeTab}
                  >
                    {config.openedItems.map((i, idx) => (
                      <Tab
                        key={i.id}
                        value={i.id}
                        label={
                          <TabLabel
                            name={tabNames?.[idx] ?? 'Undefined'}
                            onClose={() => closeTab(i.id)}
                          />
                        }
                      />
                    ))}
                  </Tabs>
                </Box>
              ) : (
                <Box minWidth={400} pr={1.5}>
                  <Alert severity="warning">No Employees Opened</Alert>
                </Box>
              )}

              <Box display="flex" flexDirection="column" pr={1.5}>
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  flexWrap="wrap"
                  gap={2}
                >
                  <Box display="flex" gap={1}>
                    <Box minWidth={400}>
                      <DateRangeSelect
                        value={
                          openedItem
                            ? [
                                dayjs(openedItem.params?.date_start).toDate(),
                                dayjs(openedItem.params?.date_end).toDate(),
                              ]
                            : undefined
                        }
                        size="small"
                        disabled={!openedItem}
                        onChange={(v) => {
                          if (openedItem) {
                            onUpdate?.(
                              update(config, {
                                openedItems: {
                                  [openedItemIndex]: {
                                    $set: {
                                      ...openedItem,
                                      selectAll: true,
                                      params: {
                                        ...openedItem.params,
                                        date_start: v?.[0]
                                          ? dayjs(v?.[0]).format('YYYY-MM-DD')
                                          : undefined,
                                        date_end: v?.[0]
                                          ? dayjs(v?.[1]).format('YYYY-MM-DD')
                                          : undefined,
                                      },
                                    },
                                  },
                                },
                              })
                            );
                          }
                        }}
                      />
                    </Box>

                    <MessageTypeSelect
                      value={openedItem?.params?.message_type}
                      size="small"
                      disabled={!openedItem}
                      options={employeeMessageTypes}
                      onChange={(v) => {
                        if (openedItem) {
                          onUpdate?.(
                            update(config, {
                              openedItems: {
                                [openedItemIndex]: {
                                  $set: {
                                    ...openedItem,
                                    selectAll: true,
                                    params: {
                                      ...openedItem.params,
                                      message_type: v,
                                    },
                                  },
                                },
                              },
                            })
                          );
                        }
                      }}
                    />
                  </Box>

                  <Box display="flex" sx={{flex: 1, minWidth: 0}}>
                    <Box sx={{flex: 1, minWidth: 0, textAlign: 'right'}}>
                      <AdaptiveButtonGroup disabled={!openedItem}>
                        <Button size="small" data-priority={1}>
                          <CommtracNodeHistoryExportButton
                            exportType="employee"
                            componentProps={{
                              ...value,
                              id: value?.activeId,
                            }}
                            params={openedItem?.params}
                            exportFields={exportFields}
                          />
                        </Button>

                        <Button
                          size="small"
                          onClick={() => params && fetchData(params)}
                          data-priority={3}
                        >
                          <RefreshIcon />
                        </Button>

                        <AutoRefreshSelect
                          value={openedItem?.refreshInterval ?? null}
                          data-priority={2}
                          onChange={(v) => {
                            if (openedItem) {
                              onUpdate?.(
                                update(config, {
                                  openedItems: {
                                    [openedItemIndex]: {
                                      $set: {
                                        ...openedItem,
                                        refreshInterval: v,
                                      },
                                    },
                                  },
                                })
                              );
                            }
                          }}
                        />

                        <Button
                          size="small"
                          onClick={() => printReport()}
                          data-priority={4}
                        >
                          <PrintIcon />
                        </Button>
                      </AdaptiveButtonGroup>
                    </Box>
                  </Box>
                </Box>
              </Box>
            </Box>

            <Backdrop
              open={fetchedInProgress}
              sx={{position: 'absolute', zIndex: 1199}}
            >
              <CircularProgress color="inherit" />
            </Backdrop>

            {fetchedErrors.map((error, idx) => (
              <Alert
                key={idx}
                severity="error"
                onClose={() => params && fetchData(params)}
              >
                {error}
              </Alert>
            ))}

            <DataGrid
              ref={dataGridRef}
              rows={rows}
              columns={columns}
              loading={fetchedInProgress}
              shownFields={shownFields}
              defaultShownFields={DEFAULT_SHOWN_FIELDS}
              columnWidths={openedItem?.columnWidths}
              columnOrder={openedItem?.columnOrder}
              pagination
              paginationMode="server"
              size="small"
              sortBy={
                params?.order
                  ? {
                      field: params?.order,
                      dir: params?.dir === 'DESC' ? 'desc' : 'asc',
                    }
                  : null
              }
              sortingMode="server"
              page={params?.page}
              pageSize={params?.limit}
              rowCount={fetchedData?.count}
              sxFooter={{
                bgcolor: (theme) =>
                  theme.palette.mode === 'dark' ? '#2E2E2E' : '#FFF',
              }}
              onPageChange={(v) => {
                if (openedItem) {
                  onUpdate?.(
                    update(config, {
                      openedItems: {
                        [openedItemIndex]: {
                          $set: {
                            ...openedItem,
                            selectAll: true,
                            params: {
                              ...openedItem.params,
                              page: v,
                            },
                          },
                        },
                      },
                    })
                  );
                }
              }}
              onPageSizeChange={(v) => {
                if (openedItem) {
                  onUpdate?.(
                    update(config, {
                      openedItems: {
                        [openedItemIndex]: {
                          $set: {
                            ...openedItem,
                            selectAll: true,
                            params: {
                              ...openedItem.params,
                              page: 0,
                              limit: v,
                            },
                          },
                        },
                      },
                    })
                  );
                }
              }}
              onSort={(v) => {
                if (v && openedItem) {
                  onUpdate?.(
                    update(config, {
                      openedItems: {
                        [openedItemIndex]: {
                          $set: {
                            ...openedItem,
                            params: {
                              ...openedItem.params,
                              order: v.field,
                              dir: v.dir === 'desc' ? 'DESC' : 'ASC',
                            },
                          },
                        },
                      },
                    })
                  );
                }
              }}
              onShownFieldsChange={handleShownFieldsChange}
              onColumnWidthsChange={handleColumnWidthChange}
              onColumnOrderAndShownFieldsChange={
                handleColumnOrderAndShownFieldsChange
              }
            />
          </Box>
        </ResizableColumns>
      </Box>
    </>
  );
};
